import { ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp, MoreHorizontal } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import Checkbox from "./Checkbox";
import EmptyState from "./EmptyState";
import { TableSkeleton } from "./Skeleton";

export type SortDir = "asc" | "desc";

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  width?: string;
  align?: "left" | "right" | "center";
  render?: (row: T) => ReactNode;
}

interface ServerPagination {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (row: T) => string | number;
  selectable?: boolean;
  actions?: (row: T) => ReactNode;
  pageSize?: number;
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  pageSizeOptions?: number[];
  selectedKeys?: (string | number)[];
  onSelectionChange?: (keys: (string | number)[]) => void;
  selectionBar?: (count: number, clear: () => void) => ReactNode;
  onRowClick?: (row: T) => void;
  /** When provided, the table renders `data` as-is (already the current page from the server)
   * and delegates page/page-size changes upward instead of paginating client-side. */
  serverPagination?: ServerPagination;
  maxHeight?: string;
  tableContainerClassName?: string;
}

function getPageWindow(totalPages: number, current: number, maxButtons = 5): number[] {
  if (totalPages <= maxButtons) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  let start = Math.max(1, current - Math.floor(maxButtons / 2));
  let end = start + maxButtons - 1;
  if (end > totalPages) {
    end = totalPages;
    start = end - maxButtons + 1;
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export default function DataTable<T>({
  columns,
  data,
  rowKey,
  selectable = false,
  actions,
  pageSize = 10,
  loading = false,
  emptyTitle = "No records found",
  emptyDescription = "There are no entries to display yet.",
  pageSizeOptions,
  selectedKeys,
  onSelectionChange,
  selectionBar,
  onRowClick,
  serverPagination,
  maxHeight,
  tableContainerClassName = "",
}: DataTableProps<T>) {
  const controlled = selectedKeys !== undefined && onSelectionChange !== undefined;
  const [page, setPage] = useState(1);
  const [pageSizeState, setPageSizeState] = useState(pageSize);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [internalSelected, setInternalSelected] = useState<(string | number)[]>([]);
  const [openMenu, setOpenMenu] = useState<string | number | null>(null);

  const selected = controlled ? selectedKeys : internalSelected;

  useEffect(() => {
    if (openMenu !== null) {
      const handler = () => setOpenMenu(null);
      document.addEventListener("click", handler);
      return () => document.removeEventListener("click", handler);
    }
  }, [openMenu]);

  const processed = useMemo(() => {
    let rows = [...data];
    if (sortKey) {
      rows.sort((a, b) => {
        const av = (a as Record<string, unknown>)[sortKey];
        const bv = (b as Record<string, unknown>)[sortKey];
        if (av === bv) return 0;
        const cmp = av == null ? -1 : bv == null ? 1 : String(av).localeCompare(String(bv), undefined, { numeric: true });
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return rows;
  }, [data, sortKey, sortDir]);

  const effectivePageSize = serverPagination ? serverPagination.pageSize : pageSizeState;
  const total = serverPagination ? serverPagination.total : processed.length;
  const totalPages = Math.max(1, Math.ceil(total / effectivePageSize));
  const safePage = serverPagination ? Math.min(serverPagination.page, totalPages) : Math.min(page, totalPages);
  const start = (safePage - 1) * effectivePageSize;
  const pageRows = serverPagination ? data : processed.slice(start, start + effectivePageSize);

  function toggleSort(key: string) {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    }
  }

  function applySelection(next: (string | number)[]) {
    if (controlled) {
      onSelectionChange?.(next);
    } else {
      setInternalSelected(next);
    }
  }

  const allSelected = pageRows.length > 0 && pageRows.every((r) => selected.includes(rowKey(r)));
  const someSelected = pageRows.some((r) => selected.includes(rowKey(r)));

  function toggleAll() {
    const pageKeys = pageRows.map(rowKey);
    if (allSelected) {
      applySelection(selected.filter((k) => !pageRows.some((r) => rowKey(r) === k)));
    } else {
      applySelection(Array.from(new Set([...selected, ...pageKeys])));
    }
  }

  function toggleOne(key: string | number) {
    applySelection(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  }

  function clearSelection() {
    applySelection([]);
  }

  function changePageSize(n: number) {
    if (serverPagination) {
      serverPagination.onPageSizeChange?.(n);
      return;
    }
    setPageSizeState(n);
    setPage(1);
  }

  function goToPage(p: number) {
    if (serverPagination) {
      serverPagination.onPageChange(p);
    } else {
      setPage(p);
    }
  }

  if (loading) {
    return <TableSkeleton rows={5} columns={columns.length + 1} />;
  }

  if (total === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  const from = total === 0 ? 0 : start + 1;
  const to = start + pageRows.length;

  return (
    <div className="overflow-hidden rounded-[10px] border border-line bg-card shadow-card">
      {selectionBar && selected.length > 0 && selectionBar(selected.length, clearSelection)}
      <div
        className={`overflow-x-auto ${maxHeight ? "overflow-y-auto" : ""} ${tableContainerClassName}`}
        style={maxHeight ? { maxHeight } : undefined}
      >
        <table className="w-full text-left border-collapse" style={{ minWidth: columns.some((c) => c.width) ? undefined : "800px" }}>
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-line">
              {selectable && (
                <th className="sticky top-0 z-10 bg-[#f8fafc] border-b border-line w-12 px-4 py-3 shadow-[0_1px_0_0_#e2e8f0]">
                  <Checkbox checked={allSelected} onChange={toggleAll} aria-label="Select all rows" />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{ width: col.width, minWidth: col.width }}
                  className={`sticky top-0 z-10 bg-[#f8fafc] border-b border-line shadow-[0_1px_0_0_#e2e8f0] px-3 py-3 text-xs font-semibold uppercase tracking-wide text-ink-secondary ${
                    col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"
                  }`}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col.key)}
                      className={`inline-flex items-center gap-1 uppercase tracking-wide cursor-pointer ${
                        sortKey === col.key ? "text-primary-600" : "text-ink-secondary hover:text-ink"
                      }`}
                    >
                      {col.header}
                      <span className="flex flex-col -space-y-1">
                        {sortKey === col.key && sortDir === "asc" ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : sortKey === col.key && sortDir === "desc" ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronsUpDown className="h-3.5 w-3.5 opacity-60" />
                        )}
                      </span>
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              ))}
              {actions && (
                <th className="sticky top-0 z-10 bg-[#f8fafc] border-b border-line shadow-[0_1px_0_0_#e2e8f0] w-12 px-2 text-right text-xs font-semibold uppercase tracking-wide text-ink-secondary">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, idx) => {
              const key = rowKey(row);
              const isSelected = selected.includes(key);
              const isNearBottom = idx >= Math.max(0, pageRows.length - 2);
              return (
                <tr
                  key={key}
                  onClick={() => onRowClick?.(row)}
                  className={`h-14 border-b border-line last:border-0 transition-colors hover:bg-page ${
                    onRowClick ? "cursor-pointer" : ""
                  }`}
                >
                  {selectable && (
                    <td className="px-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={isSelected} onChange={() => toggleOne(key)} aria-label="Select row" />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      style={{ width: col.width, minWidth: col.width }}
                      className={`px-3 text-sm text-ink ${
                        col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"
                      }`}
                    >
                      {col.render ? col.render(row) : (row[col.key] as ReactNode)}
                    </td>
                  ))}
                  {actions && (
                    <td className="relative px-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenu(openMenu === key ? null : key);
                        }}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
                        aria-label="Row actions"
                      >
                        <MoreHorizontal className="h-5 w-5" />
                      </button>
                      {openMenu === key && (
                        <div
                          className={`absolute right-2 z-30 w-44 rounded-lg border border-line bg-white py-1 shadow-dropdown animate-fadeIn ${
                            isNearBottom && pageRows.length > 2 ? "bottom-full mb-1" : "top-full mt-1"
                          }`}
                          onClick={() => setOpenMenu(null)}
                        >
                          {actions(row)}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col items-center justify-between gap-3 border-t border-line px-4 py-3 sm:flex-row">
        <p className="text-[13px] text-ink-secondary">
          Showing <span className="font-medium text-ink">{from}</span> to{" "}
          <span className="font-medium text-ink">{to}</span> of{" "}
          <span className="font-medium text-ink">{total}</span> entries
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {pageSizeOptions && (
            <div className="flex items-center gap-2">
              <span className="text-[13px] text-ink-secondary">Rows</span>
              <select
                value={effectivePageSize}
                onChange={(e) => changePageSize(Number(e.target.value))}
                className="h-8 cursor-pointer rounded-md border border-line bg-white px-2 text-[13px] text-ink focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                aria-label="Rows per page"
              >
                {pageSizeOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-center gap-1">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => goToPage(Math.max(1, safePage - 1))}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-line text-ink-secondary hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {getPageWindow(totalPages, safePage).map((p, idx, arr) => (
              <span key={p} className="flex items-center">
                {idx === 0 && p > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => goToPage(1)}
                      className="h-8 w-8 rounded-md text-[13px] font-medium text-ink-secondary hover:bg-slate-50 cursor-pointer"
                    >
                      1
                    </button>
                    {p > 2 && <span className="px-1 text-ink-muted">…</span>}
                  </>
                )}
                <button
                  type="button"
                  onClick={() => goToPage(p)}
                  className={`h-8 w-8 rounded-md text-[13px] font-medium cursor-pointer ${
                    p === safePage ? "bg-primary-500 text-white" : "text-ink-secondary hover:bg-slate-50"
                  }`}
                >
                  {p}
                </button>
                {idx === arr.length - 1 && p < totalPages && (
                  <>
                    {p < totalPages - 1 && <span className="px-1 text-ink-muted">…</span>}
                    <button
                      type="button"
                      onClick={() => goToPage(totalPages)}
                      className="h-8 w-8 rounded-md text-[13px] font-medium text-ink-secondary hover:bg-slate-50 cursor-pointer"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </span>
            ))}
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => goToPage(Math.min(totalPages, safePage + 1))}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-line text-ink-secondary hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}